import { io, Socket } from 'socket.io-client';
import { API_URL } from '../modules/auth/core/_requests';

export interface SoundEvent {
  soundFile: string;
  volume: number;
  timestamp: number;
}

export class SoundPlayerService {
  private static instance: SoundPlayerService;
  private socket: Socket | null = null;
  private audioContext: AudioContext | null = null;
  private isInitialized = false;
  private userId: number | null = null;
  private userEmail: string | null = null;
  private isWindowFocused = true;
  private focusCheckInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  public static getInstance(): SoundPlayerService {
    if (!SoundPlayerService.instance) {
      SoundPlayerService.instance = new SoundPlayerService();
    }
    return SoundPlayerService.instance;
  }

  public async initialize(userId: number, userEmail?: string): Promise<void> {
    if (this.isInitialized) return;

    this.userId = userId;
    this.userEmail = userEmail || null;
    
    // Initialize AudioContext for background playback
    this.initializeAudioContext();
    
    // Connect to Socket.IO
    this.connectSocket();
    
    // Set up focus tracking
    this.setupFocusTracking();
    
    this.isInitialized = true;
  }

  private initializeAudioContext(): void {
    try {
      // Use AudioContext for better background playback support
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // More aggressive audio context activation
      const activateAudioContext = async () => {
        if (this.audioContext?.state === 'suspended') {
          try {
            await this.audioContext.resume();
            console.log('Audio context activated');
          } catch (error) {
            console.log('Failed to resume audio context:', error);
          }
        }
      };

      // Multiple event listeners to ensure audio context is activated
      const events = ['click', 'keydown', 'touchstart', 'touchend', 'mousedown', 'mouseup'];
      events.forEach(event => {
        document.addEventListener(event, activateAudioContext, { once: false });
      });

      // Try to activate immediately if possible
      activateAudioContext();
      
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
    }
  }

  private connectSocket(): void {
    this.socket = io(API_URL, { 
      transports: ['websocket'],
      forceNew: false
    });

    this.socket.on('connect', () => {
      this.registerUser();
    });

    this.socket.on('connect_error', (error) => {
      // Silent connection error handling
    });

    this.socket.on('playRemoteSound', (event: SoundEvent) => {
      this.playSound(event);
    });

    this.socket.on('disconnect', (reason) => {
      // Silent disconnect handling
    });

    // Handle page visibility changes to maintain connection
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.socket?.disconnected) {
        this.socket.connect();
      }
    });
  }

  private async registerUser(): Promise<void> {
    if (!this.socket || !this.userId) {
      return;
    }

    // Register user for sound system using socket events (room-based)
    this.socket.emit('registerForSounds', { userId: this.userId, email: this.userEmail });
    
    // Send initial user status
    this.sendUserStatus();
  }

  private setupFocusTracking(): void {
    // Track window focus/blur
    window.addEventListener('focus', () => {
      this.isWindowFocused = true;
      this.sendUserStatus();
    });

    window.addEventListener('blur', () => {
      this.isWindowFocused = false;
      this.sendUserStatus();
    });

    // Track document visibility (tab switching)
    document.addEventListener('visibilitychange', () => {
      this.sendUserStatus();
    });

    // Send periodic status updates
    this.focusCheckInterval = setInterval(() => {
      this.sendUserStatus();
    }, 30000); // Every 30 seconds
  }

  private async sendUserStatus(): Promise<void> {
    if (!this.socket || !this.userId) return;

    const batteryInfo = await this.getBatteryInfo();
    
    const userStatus = {
      userId: this.userId,
      email: this.userEmail,
      isWindowFocused: this.isWindowFocused,
      isTabVisible: !document.hidden,
      lastActiveTime: Date.now(),
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      pageUrl: window.location.href,
      ...batteryInfo,
    };

    this.socket.emit('userStatusUpdate', userStatus);
  }

  private async getBatteryInfo(): Promise<{ batteryLevel?: number; isCharging?: boolean }> {
    try {
      if ('getBattery' in navigator) {
        const battery = await (navigator as any).getBattery();
        return {
          batteryLevel: Math.round(battery.level * 100),
          isCharging: battery.charging
        };
      }
    } catch (error) {
      // Battery API not supported or blocked
    }
    return {};
  }

    private async playSound(event: SoundEvent): Promise<void> {
    const startTime = Date.now();
    
    try {
      // Aggressively try to resume audio context
      if (this.audioContext?.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (error) {
          console.log('Could not resume audio context');
        }
      }

      // Send feedback that audio is starting
      this.sendSoundFeedback({
        soundFile: event.soundFile,
        status: 'starting',
        timestamp: startTime,
        volume: event.volume,
        windowFocused: this.isWindowFocused,
        tabVisible: !document.hidden
      });

      // Try multiple methods for maximum reliability
      let playbackSuccess = false;

      // Method 1: Try AudioContext first (better for background playback)
      try {
        await this.playWithAudioContext(event);
        playbackSuccess = true;
        this.sendSoundFeedback({
          soundFile: event.soundFile,
          status: 'success',
          timestamp: Date.now(),
          volume: event.volume,
          windowFocused: this.isWindowFocused,
          tabVisible: !document.hidden,
          playbackDelay: Date.now() - startTime,
          method: 'AudioContext'
        });
      } catch (error) {
        console.log('AudioContext playback failed, trying HTML Audio');
      }

      // Method 2: Fallback to HTML Audio if AudioContext fails
      if (!playbackSuccess) {
        try {
          const audio = new Audio(`/sounds/${event.soundFile}`);
          audio.volume = Math.min(Math.max(event.volume, 0), 1);
          
          // Aggressive settings for background playback
          audio.preload = 'auto';
          audio.crossOrigin = 'anonymous';
          
          // Force play without waiting for promise resolution
          audio.play();
          
          // Handle success/failure
          audio.addEventListener('canplaythrough', () => {
            this.sendSoundFeedback({
              soundFile: event.soundFile,
              status: 'success',
              timestamp: Date.now(),
              volume: event.volume,
              windowFocused: this.isWindowFocused,
              tabVisible: !document.hidden,
              playbackDelay: Date.now() - startTime,
              method: 'HTMLAudio'
            });
          });

          audio.addEventListener('error', (error) => {
            this.sendSoundFeedback({
              soundFile: event.soundFile,
              status: 'failed',
              timestamp: Date.now(),
              error: 'HTML Audio failed',
              windowFocused: this.isWindowFocused,
              tabVisible: !document.hidden,
              method: 'HTMLAudio'
            });
          });

          // Clean up
          audio.addEventListener('ended', () => {
            this.sendSoundFeedback({
              soundFile: event.soundFile,
              status: 'ended',
              timestamp: Date.now(),
              windowFocused: this.isWindowFocused,
              tabVisible: !document.hidden
            });
            audio.remove();
          });

        } catch (error) {
          this.sendSoundFeedback({
            soundFile: event.soundFile,
            status: 'failed',
            timestamp: Date.now(),
            error: error instanceof Error ? error.message : String(error),
            windowFocused: this.isWindowFocused,
            tabVisible: !document.hidden,
            method: 'HTMLAudio'
          });
        }
      }

    } catch (error) {
       this.sendSoundFeedback({
         soundFile: event.soundFile,
         status: 'error',
         timestamp: Date.now(),
         error: error instanceof Error ? error.message : String(error),
         windowFocused: this.isWindowFocused,
         tabVisible: !document.hidden
       });
     }
  }

  private sendSoundFeedback(feedback: any): void {
    if (!this.socket) return;
    
    this.socket.emit('soundFeedback', {
      userId: this.userId,
      email: this.userEmail,
      ...feedback
    });
  }

  private async playWithAudioContext(event: SoundEvent): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioContext not available');
    }

    try {
      // Ensure audio context is running
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const response = await fetch(`/sounds/${event.soundFile}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio file: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = audioBuffer;
      gainNode.gain.value = event.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Add event listeners for better feedback
      source.onended = () => {
        this.sendSoundFeedback({
          soundFile: event.soundFile,
          status: 'ended',
          timestamp: Date.now(),
          windowFocused: this.isWindowFocused,
          tabVisible: !document.hidden,
          method: 'AudioContext'
        });
      };
      
      source.start();
      
    } catch (error) {
      throw new Error(`AudioContext playback failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  public disconnect(): void {
    if (this.focusCheckInterval) {
      clearInterval(this.focusCheckInterval);
      this.focusCheckInterval = null;
    }
    
    if (this.socket) {
      // Socket.IO automatically handles room cleanup on disconnect
      this.socket.disconnect();
      this.socket = null;
    }
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    this.isInitialized = false;
    console.log('🔊 Sound Player Service disconnected');
  }

  public isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

// Export singleton instance
export const soundPlayerService = SoundPlayerService.getInstance(); 